<template>
  <div class="audio-player">
    <audio
      ref="audioElement"
      :src="audioSource"
      @timeupdate="updateProgress"
      @loadedmetadata="updateDuration"
      @ended="resetAudio"
    ></audio>

    <div
      v-if="compact"
      :class="[
        'audio-player--compact w-12 h-12 border-[1.5px] border-border-secondary rounded-full flex justify-center items-center',
        { 'audio-player--outlined': outlined }
      ]"
      :style="[
        progressWidth > 0
          ? `background: conic-gradient(var(--fg-brand) 0% ${progressWidth}%, var(--bg-secondary) ${progressWidth + 1}%)`
          : ''
      ]"
    >
      <div class="w-9 h-9 inner-circle rounded-full flex justify-center items-center">
        <Button
          ref="triggerRef"
          :icon="playButtonIcon"
          :aria-label="playButtonLabel"
          size="md"
          text
          class="!w-full !h-full !p-0 !bg-transparent !border-0"
          @click="onAudioPlayerClick"
        />
      </div>
      <teleport to="#dropdown-overlay">
        <div
          v-if="dropdownIsExpanded"
          ref="dropdownRef"
          v-click-away="clickAway"
          :style="floatingStyles"
          class="audio-player-dropdown-wrapper"
        >
          <div class="flex items-center">
            <Button
              :icon="playButtonIcon"
              :aria-label="playButtonLabel"
              size="md"
              plain
              severity="secondary"
              class="!mr-3 !p-1 !border-0"
              @click.stop="onAudioPlayerClick"
            />
            <div
              ref="progressContainer"
              class="progress-container w-36"
              @click.stop="scrub"
              @mousedown="startScrubbing"
              @mouseup="stopScrubbing"
              @mouseleave="stopScrubbing"
              @mousemove="mousemoveScrub"
            >
              <div
                class="progress-bar"
                :style="{ width: progressWidth + '%' }"
              ></div>
              <div
                class="progress-circle"
                :style="{ left: progressWidth + '%' }"
              ></div>
            </div>
            <div class="time-info">
              <span>{{ formatTime(currentTime) }}</span>
              <span>/</span>
              <span>{{ formatTime(duration) }}</span>
            </div>
          </div>
        </div>
      </teleport>
    </div>

    <div
      v-else
      :class="['audio-player--full', { 'audio-player--outlined': outlined }]"
    >
      <Button
        :icon="playButtonIcon"
        :aria-label="playButtonLabel"
        plain
        severity="secondary"
        @click="onAudioPlayerClick"
      />
      <div
        ref="progressContainer"
        class="progress-container"
        @click="scrub"
        @mousedown="startScrubbing"
        @mouseup="stopScrubbing"
        @mouseleave="stopScrubbing"
        @mousemove="mousemoveScrub"
      >
        <div
          class="progress-bar"
          :style="{ width: progressWidth + '%' }"
        ></div>
        <div
          class="progress-circle"
          :style="{ left: progressWidth + '%' }"
        ></div>
      </div>
      <div class="time-info">
        <span>{{ formatTime(currentTime) }}</span>
        <span>/</span>
        <span>{{ formatTime(duration) }}</span>
      </div>
      <a
        v-if="canDownload"
        :href="audioSrc"
        download
        target="_blank"
        rel="noopener noreferrer"
      >
        <Button
          icon="download"
          aria-label="Download audio file"
          size="sm"
          plain
          severity="secondary"
          class="download-button !p-1"
        />
      </a>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import Button from '@/components/button/Button.vue'
import { useFloating, autoUpdate, offset, flip } from '@floating-ui/vue'

const props = defineProps({
  audioSrc: {
    type: String,
    required: true
  },
  canDownload: {
    type: Boolean,
    default: false
  },
  compact: {
    type: Boolean,
    default: false
  },
  outlined: {
    type: Boolean,
    default: true
  }
})

const audioElement = ref(null)
const audioSource = ref(null)
const isPlaying = ref(false)
const currentTime = ref(0)
const duration = ref(0)
const progressContainer = ref(null)
const isScrubbing = ref(false)
const isLoaded = ref(false)
const triggerRef = ref(null)
const dropdownRef = ref(null)
const dropdownIsExpanded = ref(false)

const progressWidth = computed(() => (currentTime.value / duration.value) * 100)

const playButtonIcon = computed(() => {
  return isPlaying.value ? 'pause' : 'play'
})

const playButtonLabel = computed(() => {
  return isPlaying.value ? 'Pause audio' : 'Play audio'
})

const { floatingStyles } = useFloating(triggerRef, dropdownRef, {
  placement: 'top',
  middleware: [offset(15), flip()],
  strategy: 'fixed',
  whileElementsMounted: autoUpdate
})

function onAudioPlayerClick() {
  if (!isLoaded.value) {
    loadAudio().then(() => {
      togglePlay()
    })
  } else {
    togglePlay()
  }
  if (props.compact) {
    dropdownIsExpanded.value = true
  }
}

function clickAway() {
  dropdownIsExpanded.value = false
}

async function togglePlay() {
  try {
    if (isPlaying.value) {
      audioElement.value.pause()
    } else {
      await audioElement.value.play()
    }
    isPlaying.value = !isPlaying.value
  } catch (error) {
    console.error('Audio playback failed in AudioPlayer:', error)
    console.error('Audio source:', props.audioSrc)
  }
}

async function loadAudio() {
  try {
    audioSource.value = props.audioSrc

    // Add error event listener to the audio element
    if (audioElement.value) {
      audioElement.value.addEventListener('error', e => {
        console.error('Audio loading failed in AudioPlayer:', e)
        console.error('Audio source:', props.audioSrc)
      })
    }
  } catch (error) {
    console.error('Failed to load audio in AudioPlayer:', error)
    console.error('Audio source:', props.audioSrc)
  }
}

function updateProgress() {
  if (!isScrubbing.value) {
    currentTime.value = audioElement.value?.currentTime
  }
}

function updateDuration() {
  duration.value = audioElement.value?.duration
}

function resetAudio() {
  currentTime.value = 0
  isPlaying.value = false
}

function formatTime(time) {
  const minutes = Math.floor(time / 60)
  const seconds = Math.floor(time % 60)
  return minutes.toString().padStart(2, '0') + ':' + seconds.toString().padStart(2, '0')
}

function scrub(event) {
  try {
    const rect = progressContainer.value.getBoundingClientRect()
    const x = event.clientX - rect.left
    const scrubTime = (x / rect.width) * duration.value
    currentTime.value = scrubTime // updates progressWidth in real time
    audioElement.value.currentTime = scrubTime
  } catch (error) {
    console.error('Audio scrubbing failed in AudioPlayer:', error)
    console.error('Audio source:', props.audioSrc)
  }
}

function startScrubbing() {
  try {
    isScrubbing.value = true
    audioElement.value.pause()
  } catch (error) {
    console.error('Failed to pause audio during scrubbing in AudioPlayer:', error)
    console.error('Audio source:', props.audioSrc)
  }
}

function stopScrubbing() {
  try {
    if (isScrubbing.value) {
      isScrubbing.value = false
      if (isPlaying.value) {
        audioElement.value.play().catch(error => {
          console.error('Failed to resume audio after scrubbing in AudioPlayer:', error)
          console.error('Audio source:', props.audioSrc)
        })
      }
    }
  } catch (error) {
    console.error('Failed to stop scrubbing in AudioPlayer:', error)
    console.error('Audio source:', props.audioSrc)
  }
}

function mousemoveScrub(event) {
  if (isScrubbing.value) {
    scrub(event)
  }
}
</script>

<style scoped>
.audio-player-dropdown-wrapper {
  background-color: var(--bg-primary_alt);
  border-radius: var(--rounded-md);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-secondary);
  padding: var(--s-3);
}

.audio-player--full {
  display: flex;
  align-items: center;
  background-color: var(--bg-primary);
  padding: 0.5rem;
  border-radius: var(--rounded-md);
  gap: var(--s-3);
}

.progress-container {
  flex-grow: 1;
  height: 0.25rem;
  background-color: var(--utility-gray-200);
  position: relative;
  cursor: pointer;
}

.dark .progress-container {
  background-color: var(--darkmode-1);
}

.progress-bar {
  height: 100%;
  background-color: var(--fg-brand);
  position: absolute;
  left: 0;
}

.progress-circle {
  width: 1rem;
  height: 1rem;
  background-color: var(--fg-brand);
  position: absolute;
  top: -0.375rem;
  transform: translateX(-50%);
  border-radius: 50%;
  cursor: pointer;
}

.time-info {
  font-size: 0.75rem;
  margin-right: 0.5rem;
  width: 6.5rem;
  text-align: center;
  flex-shrink: 0;
}

.audio-player--compact .inner-circle {
  background-color: var(--bg-primary);
}

.dark .audio-player--compact .inner-circle {
  background-color: var(--bg-tertiary);
}

/* Outlined variant styles */
.audio-player--outlined.audio-player--compact {
  border-width: 2px;
  border-style: solid;
  border-color: var(--border-secondary);
}

.audio-player--outlined.audio-player--full {
  border: 2px solid var(--border-secondary);
}
</style>
